import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { usersApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users as UsersIcon, 
  Loader2, 
  Trash2,
  Shield,
  User,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
  KeyRound,
  Copy,
  Eye,
  EyeOff,
  UserPlus,
  Plus,
  Mail
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [passwordResetRequests, setPasswordResetRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [approveUser, setApproveUser] = useState(null);
  const [approveRole, setApproveRole] = useState('inspector');
  const [processing, setProcessing] = useState(false);
  
  // Password Reset States
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetProcessing, setResetProcessing] = useState(false);

  // Create User States
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'inspector' });
  const [creating, setCreating] = useState(false);
  const [userLimitInfo, setUserLimitInfo] = useState(null);

  useEffect(() => {
    fetchData();
    fetchUserLimitInfo();
  }, []);

  const fetchUserLimitInfo = async () => {
    try {
      const response = await usersApi.getUserLimitInfo();
      setUserLimitInfo(response.data);
    } catch (error) {
      console.error('Error fetching user limit:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [allUsersRes, pendingRes, resetRequestsRes] = await Promise.all([
        usersApi.getAll(),
        usersApi.getPending(),
        usersApi.getPasswordResetRequests()
      ]);
      setUsers(allUsersRes.data.filter(u => u.status === 'approved'));
      setPendingUsers(pendingRes.data);
      setPasswordResetRequests(resetRequestsRes.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const handlePasswordReset = async () => {
    if (!resetUser || !newPassword) return;
    setResetProcessing(true);
    
    try {
      const response = await usersApi.resetPassword(resetUser.user_id, newPassword);
      setResetResult(response.data);
      await fetchData();
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Fehler beim Zurücksetzen des Passworts');
    } finally {
      setResetProcessing(false);
    }
  };

  const handleDeleteResetRequest = async (requestId) => {
    try {
      await usersApi.deletePasswordResetRequest(requestId);
      setPasswordResetRequests(passwordResetRequests.filter(r => r.id !== requestId));
    } catch (error) {
      console.error('Error deleting request:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await usersApi.createUser(createForm);
      setShowCreateDialog(false);
      setCreateForm({ name: '', email: '', password: '', role: 'inspector' });
      await fetchData();
      await fetchUserLimitInfo();
    } catch (error) {
      alert(error.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setCreating(false);
    }
  };

  const generateUserPassword = () => {
    const chars = 'abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let password = '';
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCreateForm({ ...createForm, password });
  };

  const canCreateUser = () => {
    if (!userLimitInfo) return false;
    if (userLimitInfo.unlimited) return true;
    return userLimitInfo.remaining > 0;
  };

  const handleApprove = async (approved) => {
    if (!approveUser) return;
    setProcessing(true);
    
    try {
      await usersApi.approve(approveUser.id, approveRole, approved ? 'approved' : 'rejected');
      await fetchData();
      setApproveUser(null);
    } catch (error) {
      console.error('Error approving user:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await usersApi.delete(deleteId);
      setUsers(users.filter(u => u.id !== deleteId));
      setPendingUsers(pendingUsers.filter(u => u.id !== deleteId));
    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setDeleteId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Aktiv</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">Wartend</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Abgelehnt</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-500" data-testid="users-page">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl">Benutzerverwaltung</h1>
          <p className="text-muted-foreground mt-1">Benutzer verwalten und Registrierungen genehmigen</p>
        </div>

        {/* User Limit Info & Create Button */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          {userLimitInfo && (
            <div className="text-sm text-muted-foreground">
              {userLimitInfo.unlimited ? (
                <span>Unbegrenzte Benutzer</span>
              ) : (
                <span>
                  Benutzer: {userLimitInfo.current} / {userLimitInfo.limit}
                  {userLimitInfo.remaining > 0 && (
                    <span className="text-green-500 ml-2">({userLimitInfo.remaining} verfügbar)</span>
                  )}
                </span>
              )}
            </div>
          )}
          <Button 
            onClick={() => setShowCreateDialog(true)}
            disabled={!canCreateUser()}
            className="gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Neuer Benutzer
          </Button>
        </div>

        <Tabs defaultValue={passwordResetRequests.length > 0 ? "password-reset" : pendingUsers.length > 0 ? "pending" : "active"} className="space-y-4">
          <TabsList>
            <TabsTrigger value="password-reset" className="gap-2">
              <KeyRound className="h-4 w-4" />
              Passwort-Anfragen
              {passwordResetRequests.length > 0 && (
                <Badge className="ml-1 bg-red-500 text-white">{passwordResetRequests.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="h-4 w-4" />
              Ausstehend
              {pendingUsers.length > 0 && (
                <Badge className="ml-1 bg-amber-500 text-white">{pendingUsers.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="active" className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Aktive Benutzer
            </TabsTrigger>
          </TabsList>

          {/* Password Reset Requests Tab */}
          <TabsContent value="password-reset" className="space-y-4">
            {passwordResetRequests.length > 0 ? (
              passwordResetRequests.map((request) => (
                <Card key={request.id} className="border-border/50 border-red-500/30">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                          <KeyRound className="h-6 w-6 text-red-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{request.user_name || 'Unbekannt'}</h3>
                            <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                              Passwort-Reset
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{request.email}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Angefragt: {formatDate(request.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                          onClick={() => {
                            setResetUser(request);
                            setNewPassword('');
                            setResetResult(null);
                            generatePassword();
                          }}
                          data-testid={`reset-password-${request.id}`}
                        >
                          <KeyRound className="h-4 w-4 mr-1" />
                          Passwort setzen
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                          onClick={() => handleDeleteResetRequest(request.id)}
                          data-testid={`delete-request-${request.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Ablehnen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-border/50">
                <CardContent className="py-16">
                  <div className="text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" strokeWidth={1} />
                    <p className="text-muted-foreground">Keine offenen Passwort-Anfragen</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="pending" className="space-y-4">
            {pendingUsers.length > 0 ? (
              pendingUsers.map((user) => (
                <Card key={user.id} className="border-border/50 border-amber-500/30">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                          <Clock className="h-6 w-6 text-amber-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{user.name}</h3>
                            {getStatusBadge(user.status)}
                          </div>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                          {user.company && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {user.company}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <p className="text-xs text-muted-foreground mr-4">
                          Registriert: {formatDate(user.created_at)}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                          onClick={() => {
                            setApproveUser(user);
                            setApproveRole('inspector');
                          }}
                          data-testid={`approve-user-${user.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Genehmigen
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                          onClick={() => setDeleteId(user.id)}
                          data-testid={`reject-user-${user.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Ablehnen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-border/50">
                <CardContent className="py-16">
                  <div className="text-center">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" strokeWidth={1} />
                    <p className="text-muted-foreground">Keine ausstehenden Registrierungen</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="active" className="space-y-4">
            {users.map((user) => (
              <Card key={user.id} className="border-border/50" data-testid={`user-card-${user.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                        user.role === 'admin' ? 'bg-primary/10' : 'bg-secondary'
                      }`}>
                        {user.role === 'admin' ? (
                          <Shield className="h-6 w-6 text-primary" />
                        ) : (
                          <User className="h-6 w-6 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium">{user.name}</h3>
                          {user.id === currentUser?.id && (
                            <Badge variant="outline" className="text-xs">Sie</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        {user.company && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {user.company}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <Badge className={user.role === 'admin' ? 'bg-primary/10 text-primary border-primary/20' : ''}>
                          {user.role === 'admin' ? 'Administrator' : 'Prüfer'}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          Seit {formatDate(user.created_at)}
                        </p>
                      </div>
                      
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-red-500"
                          onClick={() => setDeleteId(user.id)}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {users.length === 0 && (
              <Card className="border-border/50">
                <CardContent className="py-16">
                  <div className="text-center">
                    <UsersIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                    <p className="text-muted-foreground">Noch keine aktiven Benutzer</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Approve Dialog */}
      <Dialog open={!!approveUser} onOpenChange={() => setApproveUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Benutzer genehmigen</DialogTitle>
            <DialogDescription>
              Wählen Sie die Rolle für {approveUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-secondary/50">
                <p className="font-medium">{approveUser?.name}</p>
                <p className="text-sm text-muted-foreground">{approveUser?.email}</p>
                {approveUser?.company && (
                  <p className="text-sm text-muted-foreground">{approveUser?.company}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Rolle zuweisen</label>
                <Select value={approveRole} onValueChange={setApproveRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inspector">Prüfer</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveUser(null)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => handleApprove(true)}
              disabled={processing}
              className="bg-green-500 hover:bg-green-600"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Genehmigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Der Benutzer wird dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Password Reset Dialog */}
      <Dialog open={!!resetUser} onOpenChange={() => {
        setResetUser(null);
        setResetResult(null);
        setNewPassword('');
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Passwort zurücksetzen
            </DialogTitle>
            <DialogDescription>
              Setzen Sie ein neues Passwort für {resetUser?.user_name || resetUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          {resetResult ? (
            <div className="py-4 space-y-4">
              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-2 text-green-500 mb-2">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-medium">Passwort erfolgreich geändert!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Teilen Sie dem Benutzer das neue Passwort mit.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-secondary/50">
                  <Label className="text-xs text-muted-foreground">Benutzer</Label>
                  <p className="font-medium">{resetResult.user_email}</p>
                </div>
                
                <div className="p-3 rounded-lg bg-secondary/50">
                  <Label className="text-xs text-muted-foreground">Neues Passwort</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="font-mono text-lg font-bold text-primary">
                      {resetResult.new_password}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => copyToClipboard(resetResult.new_password)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <Button 
                className="w-full" 
                onClick={() => {
                  setResetUser(null);
                  setResetResult(null);
                }}
              >
                Fertig
              </Button>
            </div>
          ) : (
            <div className="py-4 space-y-4">
              <div className="p-4 rounded-lg bg-secondary/50">
                <p className="text-sm">
                  <span className="font-medium">{resetUser?.user_name}</span>
                  <br />
                  <span className="text-muted-foreground">{resetUser?.email}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-password">Neues Passwort</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Neues Passwort eingeben"
                      className="pr-10 font-mono"
                      data-testid="new-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full w-10"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generatePassword}
                    data-testid="generate-password-button"
                  >
                    Generieren
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Mindestens 6 Zeichen. Klicken Sie auf "Generieren" für ein sicheres Passwort.
                </p>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setResetUser(null)}>
                  Abbrechen
                </Button>
                <Button 
                  onClick={handlePasswordReset}
                  disabled={resetProcessing || newPassword.length < 6}
                  data-testid="confirm-reset-button"
                >
                  {resetProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Passwort setzen
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md fixed top-4 left-[50%] translate-x-[-50%] translate-y-0 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Neuen Benutzer erstellen
            </DialogTitle>
            <DialogDescription>
              Erstellen Sie einen neuen Benutzer für Ihre Firma.
              {userLimitInfo && !userLimitInfo.unlimited && (
                <span className="block mt-1 text-xs">
                  Noch {userLimitInfo.remaining} Benutzer verfügbar.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateUser} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Name *</Label>
              <Input
                id="create-name"
                placeholder="Max Muster"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-email">E-Mail *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="create-email"
                  type="email"
                  placeholder="max@firma.ch"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-password">Passwort *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="create-password"
                    type="text"
                    placeholder="Sicheres Passwort"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
                <Button type="button" variant="outline" onClick={generateUserPassword}>
                  Generieren
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Mindestens 6 Zeichen. Teilen Sie das Passwort sicher mit dem Benutzer.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-role">Rolle *</Label>
              <Select 
                value={createForm.role} 
                onValueChange={(value) => setCreateForm({ ...createForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspector">Prüfer</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={creating}>
                {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Benutzer erstellen
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
